import React, { useState, useContext } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Divider,
  Modal,
  Typography
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  useKivunovaTranslation,
  stringFormatter
} from "@kivunova/kivufrontendcommon";
import ProfileEditForm from "./ProfileEditForm";
import AppButton from "../common/AppButton";
import { EditOutlined, LogoutOutlined } from "@mui/icons-material";
import useLogout from "../../hooks/api/useLogout";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";

const ProfileView: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { profileData } = useSelector((state: RootState) => state.profile);
  const [openEditModal, setOpenEditModal] = useState(false);
  const useLogoutMutation = useLogout();
  const { setAlert } = useContext(AlertBannerContext);

  if (!profileData) return null;

  const phoneDisplay = profileData.phone ? `${profileData.phone}` : "N/A";

  const handleLogout = async () => {
    try {
      await useLogoutMutation.mutateAsync();
    } catch (e) {
      console.error("Failed to logout due to error", e);
    }
  };

  const renderField = (label: string, value?: string | number | null) => (
    <Box
      sx={{ display: "flex", flexDirection: "column", mb: 2, flex: "1 1 45%" }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontWeight: 500 }}
      >
        {t(label, label.replace(/-/g, " "))}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: value ? "text.primary" : "text.disabled",
          fontWeight: 500
        }}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  );

  return (
    <>
      <Card
        data-testid="profile-view-card"
        sx={{
          borderRadius: 3,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          p: { xs: 2, sm: 3 },
          pt: { xs: 2.5, sm: 3 },
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
        }}
      >
        <CardContent sx={{ pt: 2, "&:first-of-type": { pt: 1 } }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              mb: 3,
              mt: 1
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                src={profileData.profileImage || undefined}
                alt={t("profile-avatar-alt", "User profile image")}
                sx={{ width: 70, height: 70, border: "2px solid #e0e0e0" }}
              />
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ wordBreak: "break-word" }}
                >
                  {`${profileData.firstName ?? ""} ${profileData.lastName ?? ""}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stringFormatter(profileData.role) ||
                    t("profile-role-placeholder", "No role assigned")}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "row", gap: 3 }}>
              <AppButton
                type="button"
                variant="secondary"
                onClick={handleLogout}
                color="error"
                startIcon={<LogoutOutlined />}
                sx={{ mt: { xs: 2, sm: 0 } }}
                aria-label={t("logout-button-aria", "Logout")}
              >
                {t("logout", "Logout")}
              </AppButton>

              <AppButton
                type="button"
                variant="primary"
                onClick={() => setOpenEditModal(true)}
                startIcon={<EditOutlined />}
                sx={{ mt: { xs: 2, sm: 0 } }}
                aria-label={t("edit-profile-button-aria", "Edit Profile")}
                data-testid="profile-edit-button"
              >
                {t("edit-profile", "Edit Profile")}
              </AppButton>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Info Section */}
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ mb: 2 }}
            color="text.primary"
          >
            {t("profile-information-title", "Profile Information")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 3,
              justifyContent: "flex-start"
            }}
          >
            {renderField("first-name", profileData.firstName)}
            {renderField("last-name", profileData.lastName)}
            {renderField("email", profileData.email)}
            {renderField("phone", phoneDisplay)}
            {renderField(
              "account-status",
              stringFormatter(profileData.accountStatus)
            )}
          </Box>
        </CardContent>
      </Card>
      <Modal
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        aria-labelledby="edit-profile-modal-title"
      >
        <Box
          role="dialog"
          aria-labelledby="edit-profile-modal-title"
          data-testid="profile-edit-modal"
          sx={{ outline: "none" }}
        >
          <ProfileEditForm
            onClose={() => setOpenEditModal(false)}
            setAlert={setAlert}
          />
        </Box>
      </Modal>
    </>
  );
};

export default ProfileView;
